<template>
  <FormKit
    ref="additionalPolicyholderForm"
    v-slot="{ state: { valid } }"
    v-model="formData"
    type="form"
    :actions="false"
    :incompleteMessage="false"
    :disabled="!step.isActive"
    @submit="onSubmit"
  >
    <div v-if="additionalPersonErrorMessage" class="formkit-message text-racq-error-red input-spacing">
      <InformationBox :type="InformationBoxTypes.ERROR" :description="additionalPersonErrorMessage" />
    </div>
    <FormStepInputs :step="step">
      <!-- Expose input slot to parent step -->
      <template v-for="inputId in stepInputs" :key="inputId" #[inputId]>
        <slot :name="inputId"> </slot>
      </template>
      <template #postalAddress>
        <div class="input-spacing">
          <AddressLookupOptions
            ref="postalAddressEl"
            :instanceIdentifier="instanceIdentifier"
            v-bind="step.formInputs?.postalAddress"
            :defaultIsOverseasOption="false"
            @manualAddressSelected="(address: Partial<RACQAddressResult>) => onPostalmanualAddressSelected(address)"
            @addressSelected="(address: Partial<RACQAddressResult>) => onPostalAddressSelected(address)"
            @addressReset="() => onPostalAddressReset()"
          />
        </div>
      </template>
    </FormStepInputs>
    <FormKit
      v-if="isLastAdditionalPerson"
      v-bind="formInputData('addAdditionalPerson')"
      type="button"
      inputClass="btn btn-primary btn-md input-cta input-spacing"
      outerClass="ms-auto me-2"
      :disabled="!step.isActive"
      @click="handleAddAdditionalPerson(valid)"
    />
    <FormStepFooter :step="step" :btnNextText="step.btnNext" :btnPrevText="step.btnPrev">
      <template #btnNext>
        <div class="flex ms-auto">
          <FormKit type="submit" :label="step.btnNext" :inputClass="`btn btn-primary`" />
        </div>
      </template>
    </FormStepFooter>
  </FormKit>
</template>
<script lang="ts" setup>
// Vue Core Imports
import { ref, onMounted, watch, computed, reactive, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

// Interfaces & Enums
import {
  ClaimDetailsViewModel,
  IFormStep,
  IPolicyHolderDetails,
  IQuote,
  PreviousOccurrencesViewModel,
  RACQAddressResult,
} from '@/interfaces';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { FormEvents } from '@/enums/form-events.enum';

// Stores & Composables
import { useFormStore, useJeopardyStore, useReferenceDataStore } from '@/store';
import useFormInputData from '@/composables/useFormInputData';

// Utilities & Constants
import { addOrdinalSuffix } from '@/utils/text-transforms';
import { FormAnalyticsBuilder } from '@/utils/form-analytics-builder';
import { MaxAdditional } from '@/constants/ordinal-numbers.const';
import { toBool } from '@/utils/convert';

// Third-party Libraries
import { v4 } from 'uuid';

// Components
import FormStepFooter from '@/components/FormStepFooter/FormStepFooter.vue';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import AddressLookupOptions from '@/components/AddressLookupOptions/AddressLookupOptions.vue';
import {
  mapAdditionalPolicyholdersFormToQuote,
  mapQuoteToAdditionalPolicyholdersForm,
} from '@/mappers/additional-policyholder.mapper';
import { getInsuredAddressFromQuote, getPolicyHolderPostalAddress } from '@/mappers/policyholder.mapper';
import { displayLoyaltyReview } from '@/utils/display-rules/loyalty-rules';
import { CarPurchaseAdditionalPersonType } from '@/enums/car-purchase-addl-person-type';
import { isDuplicatePolicyHolder } from '@/utils/insurance/policy-helper';
import { JP2, JP_GENERIC_CODE } from '@/constants/jeopardy-codes.const';
import { dateOfBirthIsValid } from '@/utils/formkit-helpers';
import { dateToDateString } from '@/utils/data-transforms'; //as

// Define props and emitters
// ------------------------------------------------------------- //
const instanceIdentifier = v4();
const props = withDefaults(
  defineProps<{
    step: IFormStep;
    quote: Partial<IQuote>;
    originalQuote: Partial<IQuote>;
    stepId: string;
    nextStepId: string;
    nextStepIdNoLoyalty?: string; //Note: This is required to fix a particular scenario for HP PJ342-2490.
    applyRulesAndSave: () => Promise<void>;
    mapFormToQuote?: (
      quote: Partial<IQuote>,
      formData: Record<string, any>,
      policyholderIndex: number,
      uuid?: string
    ) => void;
    mapQuoteToForm?: (
      quote: Partial<IQuote>,
      formData: Record<string, any>,
      policyholderIndex: number,
      postalAddressCompId: string
    ) => void;
    isDuplicatePolicyHolderValidator?: (
      quote: IQuote,
      firstName: string,
      lastName: string,
      dateOfBirth: any,
      stepUuid: string,
      policyholderOrDriver?: string
    ) => boolean;
  }>(),
  {
    mapFormToQuote: mapAdditionalPolicyholdersFormToQuote,
    mapQuoteToForm: mapQuoteToAdditionalPolicyholdersForm,
    isDuplicatePolicyHolderValidator: isDuplicatePolicyHolder,
  }
);

// Define Store
// ------------------------------------------------------------- //
const formStore = useFormStore();
const jpStore = useJeopardyStore();
const refDataStore = useReferenceDataStore();

const isNewPolicyHolder = ref<boolean>(false);

// Define Composables
// ------------------------------------------------------------- //
const { formInputData } = useFormInputData(props.step.formInputs!);
const customFormParams = reactive({ isAddingAdditionalPolicyholder: false });

// Define Component state
// ------------------------------------------------------------- //
const formData = ref<Record<string, any>>({});
const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
const additionalPolicyholderForm = ref(null);
const additionalPersonErrorMessage = ref('');
const claims = ref<ClaimDetailsViewModel[]>([]);
const occurrences = ref<PreviousOccurrencesViewModel[]>([]);
const postalAddressEl = ref<InstanceType<typeof AddressLookupOptions>>();
const minAge = ref<string | undefined>();

const postalAddressStore = computed(() => postalAddressEl.value?.getStore());
const stepIndex = computed(() =>
  formStore.inScopeSteps
    .filter((step) => step.id == props.stepId)
    .findIndex((step) => step.stepIndex == props.step.stepIndex)
);

const isLastAdditionalPerson = computed(() => {
  const stepActive = formStore.currentStepIndex === props.step.stepIndex;
  const additionalPeopleSteps = formStore.inScopeSteps.filter((step) => step.id === props.stepId);
  return stepIndex.value + 1 === additionalPeopleSteps.length && additionalPeopleSteps.length < MaxAdditional;
});

// Locate the current step in scope that matches the ADDITIONAL_POLICYHOLDER step for the specific step index.
// This ensures we correctly identify the step where we need to hide or show relevant input because we
// pre - loading of additional policyholders in prior steps.
const currentStepInScope = computed(() =>
  formStore.inScopeSteps.find((step) => step.id === props.stepId && step.stepIndex === props.step.stepIndex)
);

const currentAdditionalPerson = computed(() => {
  const additionalPeople = props.quote.additionalPolicyHolderDetails;

  if (!Array.isArray(additionalPeople) || stepIndex.value >= additionalPeople.length) {
    return {};
  }

  return additionalPeople.at(stepIndex.value);
});

const stepInputs = computed(() => {
  return Object.keys(props.step.formInputs || {})?.filter((inputId) => inputId != 'postalAddress');
});

// Expose form data
defineExpose({
  getFormData: () => formData.value,
});

// Lifecycle hooks
// ------------------------------------------------------------- //
onMounted(async () => {
  const showPostalSameAsInsuredAddressField = toBool(
    props.step.formInputs?.postalAddress?.showPostalSameAsInsuredAddressSelection?.value
  );
  props.mapQuoteToForm(props.quote, formData.value, stepIndex.value, instanceIdentifier);

  postalAddressStore.value?.setupAddressComponent(
    {
      isPostalAddress: true,
      allowOverseasAddress: true,
      showPostalSameAsInsuredAddressSelection: showPostalSameAsInsuredAddressField,
    },
    getInsuredAddressFromQuote(props.quote),
    getPolicyHolderPostalAddress(currentAdditionalPerson.value as IPolicyHolderDetails)
  );
  isNewPolicyHolder.value = !formData.value?.fullname.title;
  additionalPersonErrorMessage.value = '';
  claims.value = Array.isArray(formData.value.faultClaimHistory) ? [...formData.value.faultClaimHistory] : [];
  occurrences.value = Array.isArray(formData.value.previousOccurrences) ? [...formData.value.previousOccurrences] : [];

  updateAddAdditionalLabel(!isLastAdditionalPerson.value);

  await setMinAgeRestrictions();
});

// Define Functions
// ------------------------------------------------------------- //
watch(
  () => formStore.inScopeSteps,
  () => {
    updateAdditionalPersonTitle();
    updateAddAdditionalLabel(!isLastAdditionalPerson.value);
  },
  { immediate: true } // This ensures the watcher runs immediately on mount
);

watch(
  () => isLastAdditionalPerson.value,
  () => {
    updateAddAdditionalLabel(!isLastAdditionalPerson.value);
  },
  { immediate: true } // This ensures the watcher runs immediately on mount
);

//only when policyholderordriver is present ex: for car
watch(
  () => formData.value?.policyholderordriver,
  (newValue: string) => {
    const isDriver = newValue === CarPurchaseAdditionalPersonType.DRIVER;
    const isPolicyholder = newValue === CarPurchaseAdditionalPersonType.POLICYHOLDER;
    const isBoth = newValue === CarPurchaseAdditionalPersonType.BOTH;

    const shouldShowPolicyholderFields = isPolicyholder || isBoth;
    const shouldShowDriverFields = isDriver || isBoth;

    const policyholderFields = [
      'primaryContactNumber',
      'secondaryContactNumber',
      'email',
      'postalAddressDivider',
      'postalAddressLabel',
      'postalAddress',
    ];
    const driverFields = [
      'drivingHistoryDivider',
      'drivingHistoryHeading',
      'yearsOfLicenced',
      'numberOfFaultClaims',
      'numberOfPreviousOccurrence',
    ];

    // Update Policyholder or Both Details
    policyholderFields.forEach((field) =>
      formStore.updateInput(
        { inputId: field, updates: { isHidden: !shouldShowPolicyholderFields } },
        currentStepInScope.value
      )
    );

    // Update Driver or Both Details
    driverFields.forEach((field) =>
      formStore.updateInput(
        { inputId: field, updates: { isHidden: !shouldShowDriverFields } },
        currentStepInScope.value
      )
    );
  }
);

async function handleAddAdditionalPerson(valid: boolean) {
  formStore.showLoadingForCurrentStep(true);
  if (!valid) {
    const node = (additionalPolicyholderForm.value as any)?.node;
    node.submit();
    formStore.showLoadingForCurrentStep(false);
    return;
  }

  customFormParams.isAddingAdditionalPolicyholder = true;
  runBusinessRules()
    .then(async (success) => {
      customFormParams.isAddingAdditionalPolicyholder = false;
      formStore.showLoadingForCurrentStep(false);
      if (success) {
        updateAddAdditionalLabel(true);
        addAdditionalPersonStep();
        await safeProceedToNextStep();
      } else {
        showJeopardyPanel(JP_GENERIC_CODE);
      }
    })
    .catch(async (error) => {
      await handleExceptions(error);
    });
}

function updateAddAdditionalLabel(hideLabels: boolean) {
  formStore.updateInput(
    {
      inputId: 'additionalPeopleTitle',
      updates: {
        isHidden: hideLabels,
      },
    },
    currentStepInScope.value
  );

  formStore.updateInput(
    {
      inputId: 'lineBreak2',
      updates: {
        isHidden: hideLabels,
      },
    },
    currentStepInScope.value
  );

  formStore.updateInput(
    {
      inputId: 'additionalPeopleDivider',
      updates: {
        isHidden: hideLabels,
      },
    },
    currentStepInScope.value
  );
}

function duplicatePolicyHolderCheck(): Promise<boolean> {
  formStore.clearLastStepSubmitted();

  return new Promise((resolve, reject) => {
    if (
      props.isDuplicatePolicyHolderValidator(
        props.quote as IQuote,
        formData.value?.fullname?.firstName,
        formData.value?.fullname?.lastName,
        formData.value?.dateOfBirth,
        props.step.uuid ?? '',
        formData.value?.policyholderordriver
      )
    ) {
      additionalPersonErrorMessage.value = dictionary.IQ_DuplicatePolicyHolder;
      formStore.scrollToErrorMessage();
      formStore.showLoadingForCurrentStep(false);
      reject('IQ_DuplicatePolicyHolder'); // Business rule failed
    } else {
      additionalPersonErrorMessage.value = ''; // Clear previous errors
      resolve(true); // Business rule passed
    }
  });
}

async function mapAndApplyRulesAndSave(): Promise<boolean> {
  props.mapFormToQuote(props.quote, formData.value, stepIndex.value, props.step.uuid);
  await props.applyRulesAndSave();
  return true; // Rules applied successfully
}

async function runBusinessRules(): Promise<boolean> {
  try {
    await duplicatePolicyHolderCheck();

    const isSaved = await mapAndApplyRulesAndSave();
    if (!isSaved) {
      return false;
    }

    return true; // All steps succeeded
  } catch (e) {
    console.error('Error running business rules:', e);
    throw e;
  }
}

async function onSubmit() {
  const stepSubmission = formStore.submittingStep(props.step);
  if (stepSubmission.alreadySubmitted) return;

  formStore.showLoadingForCurrentStep();
  try {
    const success = await runBusinessRules();
    customFormParams.isAddingAdditionalPolicyholder = false;
    formStore.showLoadingForCurrentStep(false);
    if (success) {
      // Proceed with next actions or steps
      if (isLastAdditionalPerson.value) {
        await handleAccountCreationStep();
      } else {
        await safeProceedToNextStep();
      }
    } else {
      showJeopardyPanel(JP_GENERIC_CODE);
    }
  } catch (error) {
    await handleExceptions(error);
  }
}

/**
 * Triggers when there is no addition policyholder
 */
async function handleAccountCreationStep() {
  const existingAccountCreationStep = formStore.inScopeSteps.find((step) => step.id === 'accountcreation');
  const anyNewPolicyholder = [
    props.quote.primaryPolicyHolderDetails,
    ...(props.quote.additionalPolicyHolderDetails || []),
  ].some((person) => !person?.apttusId.value);

  if (!anyNewPolicyholder) {
    existingAccountCreationStep && formStore.removeStep(existingAccountCreationStep.stepIndex!);
    return handleLoyaltyReviewStep();
  }

  if (!existingAccountCreationStep) {
    formStore.addStepToScope('accountcreation', {
      removable: false,
      afterIndex: formStore.currentStepIndex + 1,
      withOrdinalNumber: false,
    });
  }
  await nextTick().then(() => {
    formStore.showLoadingForCurrentStep(false);
    formStore.goNextStep();
  });
}

async function handleLoyaltyReviewStep() {
  const loyaltyResponse = await displayLoyaltyReview(props.quote as IQuote, props.originalQuote as IQuote);
  const loyaltyStepExists = formStore.inScopeSteps.find((step) => step.id === props.nextStepId);

  if (!loyaltyResponse.skipLoyaltyReviewStep) {
    if (!loyaltyStepExists) {
      formStore.addStepToScope(props.nextStepId, {
        removable: false,
        afterIndex: formStore.currentStepIndex + 1,
      });
    }
    await nextTick().then(() => {
      formStore.showLoadingForCurrentStep(false);
      formStore.goNextStep();
    });
  } else if (loyaltyStepExists?.stepIndex) {
    formStore.removeStep(loyaltyStepExists.stepIndex);
    await safeProceedToNextStep();
  } else {
    await safeProceedToNextStep();
  }
}

async function safeProceedToNextStep() {
  // Wait for step dom to be created
  await nextTick().then(() => {
    //If not last additional person go next and prevent going to nextStepIdNoLoyalty prematurely.
    if (!isLastAdditionalPerson.value) {
      formStore.goNextStep();
      return;
    }
    if (props?.nextStepIdNoLoyalty) {
      const nextStep = formStore.inScopeSteps.find((x) => x.id == props.nextStepIdNoLoyalty);
      if (nextStep?.stepIndex) {
        formStore.goToStep(nextStep.stepIndex);
        return;
      }
    }

    formStore.goNextStep();
  });
}

async function handleExceptions(error: any) {
  customFormParams.isAddingAdditionalPolicyholder = false;
  formStore.showLoadingForCurrentStep(false);
  //Handle Age restrictions Jeopardy on additional step if step is already added
  if (error == JP2 && currentAdditionalPersonHasNoDobWarningOnFile()) {
    await safeProceedToNextStep();
    return;
  } else if (error == 'IQ_DuplicatePolicyHolder') {
    formStore.scrollToStep(formStore.currentStepIndex);
    return; //do nothing
  }
  showJeopardyPanel(error as string);
}

function currentAdditionalPersonHasNoDobWarningOnFile() {
  if (!currentAdditionalPerson.value) return false;
  const dateString = dateToDateString(
    (currentAdditionalPerson.value as IPolicyHolderDetails).dateOfBirth?.value,
    'yyyy-MM-dd'
  );
  return !dateOfBirthIsValid(dateString, Number(minAge.value));
}

function addAdditionalPersonStep() {
  formStore.addStepToScope(props.stepId, {
    removable: true,
    afterIndex: formStore.currentStepIndex + 1,
    uuid: v4(),
  });
}

function updateAdditionalPersonTitle() {
  const additionalPersonTitle = Object.values(props?.step?.formInputs ?? {}).find(
    (item) => item.formInputId.value === 'additionalPersonTitle'
  );

  if (!additionalPersonTitle) return;

  // Store the original title if it has not already been saved
  // to ensure the correct ordinal suffix is applied if any steps are removed.
  if (!additionalPersonTitle.originalTitle) additionalPersonTitle.originalTitle = additionalPersonTitle.title.value;

  // Update title with ordinal suffix
  additionalPersonTitle.title.value = addOrdinalSuffix(stepIndex.value, undefined, additionalPersonTitle.originalTitle);
}

function showJeopardyPanel(jpCode: string) {
  const formAnalytics = new FormAnalyticsBuilder(props.quote, FormEvents.JEOPARDY, translation);
  formAnalytics.populateJeopardy(jpCode, '');

  jpStore.showJeopardyPanel({
    jpCode: jpCode,
  });
}

function onPostalAddressSelected(address: Partial<RACQAddressResult>) {
  if (!address) return;
  formData.value.postalAddress = address;
  formData.value.postalAddress_auto = postalAddressStore.value?.buildAddressLine(address);
}

function onPostalmanualAddressSelected(address: Partial<RACQAddressResult>) {
  //Now that we keep the auto look field shown even when in manual entry, this prevents validation from firing if it were left empty.
  //and enables it to be disabled now that a selection is made.
  formData.value.postalAddress_auto = props.step?.formInputs?.postalAddress?.postContent?.value;
  formData.value.postalAddress_manual = address;
  formData.value.postalAddress = address;
}

function onPostalAddressReset() {
  formData.value.postalAddress_auto = '';
  formData.value.postalAddress_manual = {};
  formData.value.postalAddress = null;
}

async function setMinAgeRestrictions() {
  const configs = await refDataStore.getIQConfigSettings();
  const holderType = formData.value?.policyholderordriver ?? undefined;
  if (
    !holderType ||
    holderType == CarPurchaseAdditionalPersonType.POLICYHOLDER ||
    holderType == CarPurchaseAdditionalPersonType.BOTH
  ) {
    minAge.value = configs.find((c) => c.name == 'MinPolicyholderAge')?.value;
  } else if (holderType == CarPurchaseAdditionalPersonType.DRIVER) {
    minAge.value = configs.find((c) => c.name == 'MinDriverAge')?.value;
  }
  // fallback to default 16
  if (!minAge.value) minAge.value = '16';
}
</script>
